/**
 * Чтение и запись значения ячейки (§5.2, §5.5).
 *
 * Правило спеки: значение правимой ячейки читается ТОЛЬКО через наложение
 * черновика (`brandCell`/`channelCell`/`categoryCell`/`customFieldCell`),
 * иначе своя неподтверждённая правка в таблице не видна.
 */
import type {
  Brand, BrandChannelPatch, Change, ChannelCode, EnumValue, UserSummary,
} from '@shared/types.ts'
import {
  brandCell, categoryCell, channelCell, customFieldCell, type Overlay,
} from '../../../lib/draft-overlay.js'
import type { SelectOption } from '../../../components/index.js'
import type { ColumnDef } from './columns.js'
import type { TableRow } from './rows.js'
import { isApplicable } from './rows.js'

export interface ValueContext {
  overlay: Overlay
  channel: ChannelCode
  enums: Record<string, EnumValue[]>
  users: UserSummary[]
}

export interface ResolvedCell {
  value: unknown
  published: unknown
  isDraft: boolean
  label: string | null
  change: Change | null
  empty: boolean
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

/**
 * Значение ячейки с учётом черновика. `null` — колонка к строке неприменима
 * (§4.3) или читать нечего.
 */
export function readCell(ctx: ValueContext, column: ColumnDef, row: TableRow): ResolvedCell | null {
  if (!isApplicable(column, row)) return null
  const { overlay, channel } = ctx

  // Тир — единственное поле, которое в строке категории читает другой источник.
  if (row.kind === 'category') {
    if (column.code === 'tier') return wrap(categoryCell(overlay, row.category, channel, 'tier'))
    if (column.source.kind === 'custom' && column.source.scope === 'brand_category') {
      const { fieldId, code } = column.source
      return wrap(customFieldCell(overlay, 'brand_category', row.category.id, fieldId, code, row.category.fields))
    }
    return null
  }

  const brand = row.brand

  /* Колонки без источника в данных бренда: счётчик комментариев и сам
     последний комментарий. Ячейке всё равно нужен объект — иначе строка
     ничего не нарисует. */
  if (column.role === 'comments') {
    const count = brand.commentsCount
    return wrap({ value: count === 0 ? null : count, published: count, isDraft: false, label: null, change: null })
  }
  if (column.role === 'comment') {
    const body = brand.lastComment?.body ?? null
    return wrap({ value: body, published: body, isDraft: false, label: null, change: null })
  }

  switch (column.source.kind) {
    case 'brand':
      return wrap(brandCell(overlay, brand, column.source.field))
    case 'channel':
      return wrap(channelCell(overlay, brand, channel, column.source.field))
    case 'custom': {
      const { fieldId, code, scope } = column.source
      if (scope === 'brand') {
        return wrap(customFieldCell(overlay, 'brand', brand.id, fieldId, code, brand.fields))
      }
      if (scope === 'brand_channel') {
        const values = brand.channels[channel]
        if (!values) return null
        return wrap(customFieldCell(overlay, 'brand_channel', brand.id, fieldId, code, values.fields))
      }
      return null
    }
    default:
      return null
  }
}

function wrap(cell: { value: unknown; published: unknown; isDraft: boolean; label: string | null; change: Change | null }): ResolvedCell {
  return { ...cell, empty: isEmptyValue(cell.value) }
}

// ───────────────────────────── справочники ───────────────────────────────────

/** Строки поповера справочника в порядке справочника. */
export function enumOptions(
  enums: Record<string, EnumValue[]>, setCode: string | undefined,
): SelectOption[] {
  if (!setCode) return []
  const values = enums[setCode] ?? []
  return values
    .filter((value) => value.isActive)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((value) => ({
      value: value.value,
      label: value.label,
      ...(value.hint ? { hint: value.hint } : null),
    }))
}

/**
 * Цвет чипа значения. Хранится в самом справочнике: покрасить новое значение
 * можно в «Справочниках», код для этого править не нужно.
 */
export function enumColor(
  enums: Record<string, EnumValue[]>, setCode: string | undefined, value: unknown,
): string | null {
  if (!setCode || typeof value !== 'string' || value === '') return null
  return (enums[setCode] ?? []).find((item) => item.value === value)?.color ?? null
}

export function enumLabel(
  enums: Record<string, EnumValue[]>, setCode: string | undefined, value: unknown,
): string | null {
  if (!setCode || typeof value !== 'string' || value === '') return null
  const hit = (enums[setCode] ?? []).find((item) => item.value === value)
  return hit?.label ?? value
}

export function userOptions(users: UserSummary[]): SelectOption[] {
  return users
    .filter((user) => user.isActive)
    .map((user) => ({ value: String(user.id), label: user.fullName }))
}

// ───────────────────────────── URL (§5.2) ────────────────────────────────────

/**
 * Свои хосты: сам сайт и его поддомены — розничный high-level.ru и оптовый
 * lk.high-level.ru. У своих адрес показывается одним путём: хост в каждой
 * строке повторяет то, что и так написано в переключателе канала, и съедает
 * половину узкой колонки. У чужого хост остаётся — иначе строка солжёт.
 */
const OWN_HOST = 'high-level.ru'

function isOwnHost(host: string): boolean {
  return host === OWN_HOST || host.endsWith(`.${OWN_HOST}`)
}

export interface UrlView {
  /** Что показываем в ячейке: путь для своего хоста, адрес целиком для чужого. */
  text: string
  /**
   * Чей это адрес, если не основной сайт: «lk» у оптового кабинета.
   * Без этой пометки строки Розницы и Опта выглядели одинаково — путь-то
   * у них один, и казалось, что в обоих каналах лежит одна и та же ссылка.
   */
  site: string | null
  href: string | null
  valid: boolean
}

export function urlView(raw: unknown): UrlView {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { text: '', site: null, href: null, valid: true }
  }
  const value = raw.trim()
  try {
    const parsed = new URL(value.startsWith('http') ? value : `https://${value}`)
    const host = parsed.hostname.replace(/^www\./, '')
    const path = parsed.pathname === '/' ? '/' : parsed.pathname.replace(/\/$/, '')
    const own = isOwnHost(host)
    const sub = own && host !== OWN_HOST ? host.slice(0, -(OWN_HOST.length + 1)) : null
    return {
      text: own ? path + parsed.search : host + path + parsed.search,
      site: sub,
      href: parsed.toString(),
      valid: true,
    }
  } catch {
    return { text: value, site: null, href: null, valid: false }
  }
}

/** Нормализация вставленного адреса: кавычки, пробелы, протокол (§5.2). */
export function normalizeUrl(raw: string): string {
  const value = raw.trim().replace(/^["'«»]+|["'«»]+$/g, '').replace(/\s+/g, '')
  if (value === '') return ''
  if (/^https?:\/\//i.test(value)) return value
  return `https://${value}`
}

export function isUrlLike(value: string): boolean {
  if (value === '') return true
  try {
    const parsed = new URL(normalizeUrl(value))
    return parsed.hostname.includes('.')
  } catch {
    return false
  }
}

// ───────────────────────────── форматы ──────────────────────────────────────

export function dateView(value: unknown): string {
  if (typeof value !== 'string' || value === '') return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function timeView(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

/** Подпись прежнего значения для тултипа «было: B · 11:42 · видите только вы». */
export function previousLabel(
  ctx: ValueContext, column: ColumnDef, cell: ResolvedCell,
): string {
  const change = cell.change
  if (change?.oldLabel) return change.oldLabel
  const published = cell.published
  if (published === null || published === undefined || published === '') return '—'
  if (column.enumSet) return enumLabel(ctx.enums, column.enumSet, published) ?? String(published)
  if (typeof published === 'boolean') return published ? 'Да' : 'Нет'
  if (column.role === 'url') return urlView(published).text
  return String(published)
}

/** Подпись значения для панели массовых операций и тостов. */
export function valueLabel(ctx: ValueContext, column: ColumnDef, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (column.enumSet) return enumLabel(ctx.enums, column.enumSet, value) ?? String(value)
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет'
  return String(value)
}

// ───────────────────────────── запись правки ────────────────────────────────

/** Действия стора, которыми пишется правка. Передаются снаружи: слой значений
 *  не знает про стор и остаётся проверяемым. */
export interface WriteActions {
  editBrand: (
    brandId: number,
    input: { name?: string; categoriesUnclear?: boolean; fields?: Record<string, unknown> },
  ) => Promise<void>
  editChannel: (brandId: number, channel: ChannelCode, patch: BrandChannelPatch) => Promise<void>
  editCategoryChannel: (
    brandId: number, brandCategoryId: number, channel: ChannelCode,
    input: { isActive?: boolean; tier?: string | null },
  ) => Promise<void>
}

/**
 * Запись значения ячейки в черновик (§5.5): правка уходит на сервер в момент
 * фиксации, состояния «несохранено» не существует. Какой именно вызов нужен,
 * решает источник колонки — строка об этом ничего не знает.
 */
export async function writeCell(
  actions: WriteActions, channel: ChannelCode, column: ColumnDef, row: TableRow, value: unknown,
): Promise<void> {
  if (row.kind === 'category') {
    if (column.code === 'tier') {
      await actions.editCategoryChannel(row.brand.id, row.category.id, channel, {
        tier: typeof value === 'string' && value !== '' ? value : null,
      })
    }
    // Пользовательские поля со scope «категория бренда» правятся в карточке:
    // в сторе для них действия пока нет (см. отчёт).
    return
  }
  if (row.kind !== 'brand') return

  const brandId = row.brand.id
  switch (column.source.kind) {
    case 'brand': {
      if (column.source.field === 'name') {
        if (typeof value === 'string' && value.trim() !== '') await actions.editBrand(brandId, { name: value.trim() })
        return
      }
      await actions.editBrand(brandId, { categoriesUnclear: value === true })
      return
    }
    case 'channel': {
      const patch: BrandChannelPatch = {}
      ;(patch as Record<string, unknown>)[column.source.field] = value
      await actions.editChannel(brandId, channel, patch)
      return
    }
    case 'custom': {
      const { code, scope } = column.source
      if (scope === 'brand') {
        await actions.editBrand(brandId, { fields: { [code]: value } })
        return
      }
      if (scope === 'brand_channel') {
        await actions.editChannel(brandId, channel, { fields: { [code]: value } })
      }
      return
    }
    default:
      return
  }
}
